import { useState } from "react";
import { Link } from "react-router-dom";
import useOperationsSnapshot from "@/hooks/useOperationsSnapshot";
import {
  Activity, ShieldCheck, Scale, Mail, Users, Zap, Cpu, Bell,
  Layers, Target, ClipboardList, TrendingUp, ArrowRight, Wifi, WifiOff,
  Loader2, ExternalLink,
} from "lucide-react";
import "@/pages/admin/AdminEmailDelivery.css";
import MetricHelp from "@/components/MetricHelp";
import { getMetricHelp } from "@/lib/metricHelp";

const WIDGET_ICONS = {
  platform_health: Cpu,
  security_posture: ShieldCheck,
  compliance_status: Scale,
  email_infrastructure: Mail,
  partner_activity: Users,
  api_health: Zap,
  background_jobs: Layers,
  system_alerts: Bell,
  tenant_health: Layers,
  operational_kpis: ClipboardList,
  audit_summary: Activity,
  executive_metrics: TrendingUp,
};

const STATUS_TONE = {
  healthy: { bg: "#DCFCE7", fg: "#166534", accent: "#22C55E", label: "Healthy" },
  warning: { bg: "#FEF3C7", fg: "#92400E", accent: "#F59E0B", label: "Warning" },
  critical: { bg: "#FEE2E2", fg: "#991B1B", accent: "#EF4444", label: "Critical" },
  unknown: { bg: "#F1F5F9", fg: "#475569", accent: "#94A3B8", label: "Unknown" },
};

const VIEW_LABELS = {
  admin: "Administrator (all)",
  executive_director: "Executive Director",
  board: "Board",
  operations: "Operations",
  security_compliance: "Security & Compliance",
};

function EventSeverityDot({ severity }) {
  const c = {
    success: "#22C55E", warning: "#F59E0B",
    notable: "#3B82F6", info: "#94A3B8",
  }[severity] || "#94A3B8";
  return <span style={{ width: 8, height: 8, borderRadius: 999, background: c, display: "inline-block" }} />;
}

function StatusPill({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.unknown;
  return (
    <span style={{ background: t.bg, color: t.fg,
                    padding: "3px 10px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: "0.06em", textTransform: "uppercase" }}
          data-testid={`eoc-status-${status}`}>
      {t.label}
    </span>
  );
}

function Widget({ w }) {
  const Icon = WIDGET_ICONS[w.id] || Activity;
  const tone = STATUS_TONE[w.status] || STATUS_TONE.unknown;
  const helpTopic = `operations_${w.id}`;
  const hasHelp = !!getMetricHelp(helpTopic);
  const inner = (
    <div
      data-testid={`eoc-widget-${w.id}`}
      style={{
        background: "#fff", borderLeft: `4px solid ${tone.accent}`,
        borderRadius: 14, padding: "16px 18px",
        border: "1px solid #E5E5E0", height: "100%",
        display: "flex", flexDirection: "column", gap: 8,
        transition: "all 0.15s ease",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={16} color={tone.accent} />
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
                          color: "#64748B", fontWeight: 700 }}>
            {w.category}
          </div>
          {hasHelp && (
            <span onClick={(e) => e.stopPropagation()}>
              <MetricHelp topicKey={helpTopic} size="sm" side="bottom" align="start" />
            </span>
          )}
        </div>
        <StatusPill status={w.status} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{w.title}</div>
      {w.primary_metric ? (
        <div>
          <div style={{ fontSize: 11, color: "#64748B", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {w.primary_metric.label}
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#0F172A", marginTop: 2 }}
                data-testid={`eoc-widget-${w.id}-primary`}>
            {w.primary_metric.value}
          </div>
        </div>
      ) : null}
      {w.secondary_metrics?.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginTop: 4 }}>
          {w.secondary_metrics.slice(0, 4).map((m, i) => (
            <div key={i} style={{ fontSize: 12, color: "#475569" }}>
              <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8" }}>
                {m.label}
              </div>
              <div style={{ fontWeight: 700, color: "#0F172A" }}>{m.value ?? "—"}</div>
            </div>
          ))}
        </div>
      ) : null}
      {w.deep_link ? (
        <div style={{ marginTop: "auto", paddingTop: 8 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                            fontSize: 12, fontWeight: 600, color: "#1E3A8A" }}>
            View details <ArrowRight size={12} />
          </span>
        </div>
      ) : null}
    </div>
  );
  return w.deep_link ? (
    <Link to={w.deep_link} style={{ textDecoration: "none", color: "inherit", display: "block", height: "100%" }}
          data-testid={`eoc-deeplink-${w.id}`}>
      {inner}
    </Link>
  ) : inner;
}

function ConnectionBadge({ mode }) {
  const meta = {
    sse: { icon: Wifi, bg: "#DCFCE7", fg: "#166534", label: "Live (SSE)" },
    polling: { icon: Wifi, bg: "#DBEAFE", fg: "#1E40AF", label: "Polling (fallback)" },
    connecting: { icon: Loader2, bg: "#FEF3C7", fg: "#92400E", label: "Connecting…" },
    error: { icon: WifiOff, bg: "#FEE2E2", fg: "#991B1B", label: "Disconnected" },
  }[mode] || {};
  const Icon = meta.icon || Wifi;
  return (
    <span style={{ background: meta.bg, color: meta.fg,
                    padding: "4px 10px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                    textTransform: "uppercase", display: "inline-flex",
                    gap: 4, alignItems: "center" }}
          data-testid={`eoc-conn-${mode}`}>
      <Icon size={11} className={mode === "connecting" ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
}

function EventFeed({ events }) {
  return (
    <div className="email-infra-card" style={{ height: "100%" }} data-testid="eoc-event-feed">
      <div className="email-infra-card__title">
        <Activity size={16} /> Live event stream ({events.length})
      </div>
      <div style={{ maxHeight: 500, overflowY: "auto" }}>
        {events.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", padding: 12 }}>
            No recent events (last 24h).
          </div>
        ) : events.map((e, i) => (
          <div key={e.id || i}
                style={{ display: "flex", gap: 8, alignItems: "start",
                          padding: "8px 4px", borderBottom: "1px solid #F0EFEB", fontSize: 12 }}
                data-testid={`eoc-event-${i}`}>
            <EventSeverityDot severity={e.severity} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "#0F172A" }}>
                <code style={{ fontSize: 11 }}>{e.action}</code>
              </div>
              <div style={{ color: "#64748B", fontSize: 11, marginTop: 2 }}>
                {e.actor} · {e.resource_type || "system"} · {new Date(e.ts).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExecutiveOperationsCenter() {
  const [view, setView] = useState("admin");
  const { snapshot, connectionMode, refresh } = useOperationsSnapshot(view);

  return (
    <div className="email-infra-shell" data-testid="executive-operations-center">
      <div className="email-infra-hero" style={{
        background: "linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
          <div>
            <div className="email-infra-hero__eyebrow">Executive Operations Center</div>
            <h1 className="email-infra-hero__title">Command Center</h1>
            <div className="email-infra-hero__sub">
              Unified operational view for HarborWell leadership. Live event stream updates
              every 15s (SSE) with polling fallback.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            <ConnectionBadge mode={connectionMode} />
            {snapshot ? (
              <span style={{ background: "rgba(255,255,255,0.12)", padding: "3px 10px",
                                borderRadius: 999, fontSize: 11, color: "#fff",
                                letterSpacing: "0.06em", textTransform: "uppercase",
                                fontWeight: 700 }}
                    data-testid="eoc-overall-status">
                Overall: {snapshot.overall_status}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="email-infra-controls">
        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>View:</label>
        <select value={view} onChange={e => setView(e.target.value)} data-testid="eoc-view-select">
          {Object.entries(VIEW_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <button className="email-infra-btn email-infra-btn--secondary" onClick={refresh}
                data-testid="eoc-refresh-btn">
          Refresh now
        </button>
      </div>

      {!snapshot ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>
          <Loader2 size={22} className="animate-spin" style={{ marginRight: 8 }} />
          Loading command center…
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          {/* Widget grid */}
          <div>
            <div style={{ display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                              gap: 12 }}
                  data-testid="eoc-widget-grid">
              {snapshot.widgets.map(w => <Widget key={w.id} w={w} />)}
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>
              Snapshot computed at {new Date(snapshot.computed_at).toLocaleTimeString()}
              · view: <strong>{snapshot.view}</strong>
              · {snapshot.widgets.length} widgets
            </div>
          </div>

          {/* Live event feed */}
          <EventFeed events={snapshot.events || []} />
        </div>
      )}
    </div>
  );
}
